import dayjs from 'dayjs'
import { type FormikErrors, type FormikTouched, useFormik } from 'formik'
import { useCallback, useEffect, useState, Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { RoutePath } from '@percent/cause-dashboard/app/routes/Routes'
import { useCausesPortalAnalytics, useMutation } from '@percent/cause-dashboard/common/hooks'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import type {
  EditVolunteeringOpportunityPayload,
  OpportunityDetailDto
} from '@percent/cause-dashboard/services/volunteering/volunteeringService.types'
import {
  type UseErrorModalReturn,
  type UseSuccessModalReturn,
  type UseModalReturn,
  useCoverImageField,
  useErrorModal,
  useSuccessModal,
  useModal
} from '@percent/domain/giving'
import { type OpportunityLocationDto, OpportunityLocationTypeDto, OpportunityType } from '@percent/domain/volunteering'
import { getTimeFromDate } from '@percent/cause-dashboard/common/utility/getDateFromDateAndTime'

import { FILE_SIZE_2MB, FILE_SIZE_2MB_HUMAN, ACCEPTED_BANNER_IMG_FORMATS_HUMAN } from '../constants'
import { useMapError } from '../hooks/useMapError'
import { usePartnerConfig, type UsePartnerConfigResult } from '../hooks/usePartnerConfig'
import { addProtocolToWebsiteUrl } from '../utils/addProtocolToUrl'
import { formatStartEndDate } from '../utils/formatStartEndDate'
import { useOpportunityFormValidationSchema } from '../hooks/useOpportunityFormValidation'
import type { EditOpportunityFormFields } from './EditOpportunityForm.types'

export const getInitialValues = (): EditOpportunityFormFields => ({
  image: '',
  name: '',
  date: undefined,
  startTime: '',
  endTime: '',
  description: '',
  locationType: '',
  locationUrl: '',
  participantSpots: undefined,
  activities: [],
  skills: [],
  addressLineOne: '',
  addressLineTwo: '',
  city: '',
  country: '',
  zipCode: '',
  long: 0,
  lat: 0,
  notificationsEnabled: true,
  targetCountries: []
})

export const mapFormValuesToEditEventPayload = (
  formValues: EditOpportunityFormFields,
  coverImage?: File
): EditVolunteeringOpportunityPayload => {
  const {
    date,
    startTime,
    endTime,
    name,
    description,
    activities,
    skills,
    participantSpots,
    notificationsEnabled,
    targetCountries,
    locationType,
    locationUrl,
    addressLineOne,
    addressLineTwo,
    city,
    country,
    zipCode,
    long,
    lat
  } = formValues

  const { startDate, endDate } = formatStartEndDate(date, startTime, endTime)

  const trimmedLocationUrl = locationUrl.trim()

  const location: OpportunityLocationDto =
    locationType === OpportunityLocationTypeDto.Virtual
      ? {
          type: OpportunityLocationTypeDto.Virtual,
          link: trimmedLocationUrl.length ? addProtocolToWebsiteUrl(trimmedLocationUrl) : undefined
        }
      : {
          type: OpportunityLocationTypeDto.Offline,
          addressLineOne,
          addressLineTwo,
          city,
          country,
          zipCode,
          long,
          lat
        }

  return {
    name,
    description,
    location,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    startDate: startDate!,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    endDate: endDate!,
    participantSpots,
    timeTracking: 'automatic',
    notificationsEnabled,
    skills,
    activities,
    image: coverImage,
    targetCountries
  }
}

export interface UseEditOpportunityFormProps {
  opportunityData?: OpportunityDetailDto
}

export interface UseEditOpportunityFormResult {
  coverImage: File | undefined
  coverImageError: string | undefined
  handleCoverImageChange: (file: File | string, fileName?: string) => Promise<void>
  isValid: boolean
  errors: FormikErrors<EditOpportunityFormFields>
  values: EditOpportunityFormFields
  handleChange: (e: React.ChangeEvent<Element>) => void
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
  handleBlur: (e: React.FocusEvent<Element>) => void
  touched: FormikTouched<EditOpportunityFormFields>
  isSubmitting: boolean
  setFieldValue: <T extends keyof EditOpportunityFormFields>(
    field: T,
    value: EditOpportunityFormFields[T],
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<EditOpportunityFormFields>>
  setFieldTouched: (
    field: string,
    touched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<EditOpportunityFormFields>>
  dirty: boolean
  formValid: boolean
  validateField: (name: string) => Promise<void> | Promise<string | undefined>
  validateFields: (keys: string[]) => Promise<void>
  partnerConfig: UsePartnerConfigResult
  successModalProps: UseSuccessModalReturn
  errorModalProps: UseErrorModalReturn
  loadPartnerConfigErrorModal: UseErrorModalReturn
  cancellationModalProps: UseModalReturn
  handleCancelForm: VoidFunction
  setInitialFormValues: Dispatch<SetStateAction<EditOpportunityFormFields>>
  setDateValue: (value: EditOpportunityFormFields['date']) => void
  setStartTime: (value: EditOpportunityFormFields['startTime']) => void
  setEndTime: (value: EditOpportunityFormFields['endTime']) => void
  setDateTouched: () => void
  setStartTimeTouched: () => void
  setEndTimeTouched: () => void
  setSkills: (value: EditOpportunityFormFields['skills']) => void
  setActivities: (value: EditOpportunityFormFields['activities']) => void
  setParticipantSpots: (value: EditOpportunityFormFields['participantSpots']) => void
  setNotificationsEnabled: (value: EditOpportunityFormFields['notificationsEnabled']) => void
  setDescription: (value: EditOpportunityFormFields['description']) => void
  setDescriptionTouched: () => void
}

export const mapOpportunityDataToFormFields = (opportunityData: OpportunityDetailDto): EditOpportunityFormFields => {
  const startDate = opportunityData.startDate ? dayjs(opportunityData.startDate) : undefined
  const endDate = opportunityData.endDate ? dayjs(opportunityData.endDate) : undefined

  return {
    image: opportunityData.image ?? '',
    name: opportunityData.name,
    description: opportunityData.description || '',
    date: startDate && endDate ? [new Date(startDate.toISOString()), new Date(endDate.toISOString())] : undefined,
    startTime: getTimeFromDate(startDate),
    endTime: getTimeFromDate(endDate),
    locationType: opportunityData.location.type || '',
    locationUrl:
      opportunityData.location.type === OpportunityLocationTypeDto.Virtual ? opportunityData.location.link || '' : '',
    participantSpots: opportunityData.spots ?? undefined,
    activities: opportunityData.activities?.map(activity => activity.id) || [],
    skills: opportunityData.skills?.map(skill => skill.id) || [],
    addressLineOne:
      opportunityData.location.type === OpportunityLocationTypeDto.Offline
        ? opportunityData.location.addressLineOne || ''
        : '',
    addressLineTwo:
      opportunityData.location.type === OpportunityLocationTypeDto.Offline
        ? opportunityData.location.addressLineTwo || ''
        : '',
    city:
      opportunityData.location.type === OpportunityLocationTypeDto.Offline ? opportunityData.location.city || '' : '',
    country:
      opportunityData.location.type === OpportunityLocationTypeDto.Offline
        ? opportunityData.location.country || ''
        : '',
    zipCode:
      opportunityData.location.type === OpportunityLocationTypeDto.Offline
        ? opportunityData.location.zipCode || ''
        : '',
    long: opportunityData.location.type === OpportunityLocationTypeDto.Offline ? opportunityData.location.long || 0 : 0,
    lat: opportunityData.location.type === OpportunityLocationTypeDto.Offline ? opportunityData.location.lat || 0 : 0,
    notificationsEnabled: opportunityData.notificationsEnabled || false,
    targetCountries: opportunityData.targetCountries || []
  }
}

export const useEditOpportunityForm = ({
  opportunityData
}: UseEditOpportunityFormProps): UseEditOpportunityFormResult => {
  const { t } = useTranslation()
  const { track } = useCausesPortalAnalytics()
  const { volunteeringService } = useServices()
  const navigate = useNavigate()
  const validationSchema = useOpportunityFormValidationSchema()
  const mapError = useMapError()

  const [initialFormValues, setInitialFormValues] = useState<EditOpportunityFormFields>(getInitialValues())

  useEffect(() => {
    if (opportunityData) {
      setInitialFormValues(mapOpportunityDataToFormFields(opportunityData))
    }
  }, [opportunityData])

  const successModalProps = useSuccessModal()
  const errorModalProps = useErrorModal()
  const loadPartnerConfigErrorModal = useErrorModal()
  const cancellationModalProps = useModal()

  const { coverImage, coverImageError, setCoverImageError, handleCoverImageChange } = useCoverImageField({
    maxFileSize: FILE_SIZE_2MB,
    validationErrorRequiredField: t('validation.requiredField'),
    validationErrorInvalidFileType: t('validation.invalidFileType', {
      fileFormats: ACCEPTED_BANNER_IMG_FORMATS_HUMAN
    }),
    validationErrorFileTooLarge: t('validation.fileTooLarge', { fileSize: FILE_SIZE_2MB_HUMAN })
  })

  const [{ isLoading: isSubmitting }, { apiFunc: editOpportunityMutate }] = useMutation(
    (payload: EditVolunteeringOpportunityPayload) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      volunteeringService.editVolunteeringOpportunity(opportunityData!.id, payload),
    successModalProps.openModal,
    error => {
      const { errorMessage, reason } = mapError(error)
      console.error(reason, errorMessage)
      errorModalProps.setErrorMsg(errorMessage)
      errorModalProps.openModal()
    }
  )

  const [{ isLoading: isUpdatingLocation }, { apiFunc: editLocationMutate }] = useMutation(
    (payload: OpportunityLocationDto) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      volunteeringService.editVolunteeringOpportunityLocation(opportunityData!.id, payload),
    undefined,
    error => {
      const { errorMessage, reason } = mapError(error)
      console.error(reason, errorMessage)
      errorModalProps.setErrorMsg(errorMessage)
      errorModalProps.openModal()
    }
  )

  const {
    isValid,
    errors,
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    touched,
    setFieldValue,
    setFieldTouched,
    validateForm,
    validateField
  } = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async data => {
      const targetCountries = data.targetCountries.length ? data.targetCountries : partnerConfig.allCountries

      const payload = mapFormValuesToEditEventPayload({ ...data, targetCountries }, coverImage)

      if (opportunityData && payload.location && opportunityData.location.type !== payload.location.type) {
        editLocationMutate(payload.location)
      }

      editOpportunityMutate(payload)

      track('Volunteering Opportunity Edit Completed', {
        properties: {
          opportunityLocationType: data.locationType,
          opportunityType: OpportunityType.Event
        }
      })
    },
    validateOnBlur: true,
    validateOnChange: true
  })

  const setDateValue = useCallback(
    (value: EditOpportunityFormFields['date']) => setFieldValue('date', value),
    [setFieldValue]
  )
  const setStartTime = useCallback(
    (value: EditOpportunityFormFields['startTime']) => setFieldValue('startTime', value),
    [setFieldValue]
  )
  const setEndTime = useCallback(
    (value: EditOpportunityFormFields['endTime']) => setFieldValue('endTime', value),
    [setFieldValue]
  )
  const setDateTouched = useCallback(() => setFieldTouched('date', true), [setFieldTouched])
  const setStartTimeTouched = useCallback(() => setFieldTouched('startTime', true), [setFieldTouched])
  const setEndTimeTouched = useCallback(() => setFieldTouched('endTime', true), [setFieldTouched])
  const setSkills = useCallback(
    (value: EditOpportunityFormFields['skills']) => setFieldValue('skills', value),
    [setFieldValue]
  )
  const setActivities = useCallback(
    (value: EditOpportunityFormFields['activities']) => setFieldValue('activities', value),
    [setFieldValue]
  )
  const setParticipantSpots = useCallback(
    (value: EditOpportunityFormFields['participantSpots']) => setFieldValue('participantSpots', value),
    [setFieldValue]
  )
  const setNotificationsEnabled = useCallback(
    (value: EditOpportunityFormFields['notificationsEnabled']) => setFieldValue('notificationsEnabled', value),
    [setFieldValue]
  )
  const setDescription = useCallback(
    (value: EditOpportunityFormFields['description']) => setFieldValue('description', value),
    [setFieldValue]
  )
  const setDescriptionTouched = useCallback(() => setFieldTouched('description', true), [setFieldTouched])

  const validateFields = useCallback(
    async (keys: string[]) => {
      await Promise.all(keys.map(key => validateField(key)))
    },
    [validateField]
  )

  const partnerConfig = usePartnerConfig(opportunityData?.partnerId)

  useEffect(() => {
    if (partnerConfig.errors?.length) {
      console.error(partnerConfig.errors)
      loadPartnerConfigErrorModal.setErrorMsg(t('errorMessage.somethingWentWrong'))
      loadPartnerConfigErrorModal.openModal()
    }
  }, [partnerConfig.errors, loadPartnerConfigErrorModal, t])

  const coverImageChanged = !!coverImage

  const isFormDirty = (
    newValues: EditOpportunityFormFields,
    initialValues: EditOpportunityFormFields,
    coverImgChanged: boolean
  ) => {
    if (coverImgChanged) return true
    const allKeys = new Set([...Object.keys(newValues), ...Object.keys(initialValues)]) as Set<
      keyof EditOpportunityFormFields
    >

    for (const key of allKeys) {
      if (newValues[key] !== initialValues[key]) return true
    }

    return false
  }

  const formDirty = isFormDirty(values, initialFormValues, coverImageChanged)

  const formValid =
    isValid &&
    (formDirty || coverImageChanged) &&
    (!!coverImage || !!initialFormValues.image) &&
    !coverImageError &&
    !isSubmitting &&
    !isUpdatingLocation

  useEffect(() => {
    if (touched.image && !coverImage) {
      setCoverImageError(t('validation.requiredField'))
    }
  }, [coverImage, t, touched.image, setCoverImageError])

  useEffect(() => {
    validateForm()
  }, [validateForm, values.locationType])

  const handleSuccessModalClose = () => {
    successModalProps.closeModal()
    navigate(RoutePath.VOLUNTEERING)
  }

  const handleCancelForm = () => {
    if (formDirty || !!coverImage) {
      cancellationModalProps.openModal()
    } else {
      navigate(-1)
    }
  }

  return {
    coverImage,
    coverImageError,
    handleCoverImageChange,
    isValid,
    errors,
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    touched,
    isSubmitting: isSubmitting || isUpdatingLocation,
    setFieldValue,
    setFieldTouched,
    setDateValue,
    setStartTime,
    setEndTime,
    setDateTouched,
    setStartTimeTouched,
    setEndTimeTouched,
    setSkills,
    setActivities,
    setParticipantSpots,
    setNotificationsEnabled,
    setDescription,
    setDescriptionTouched,
    dirty: formDirty,
    formValid,
    validateField,
    validateFields,
    partnerConfig,
    successModalProps: { ...successModalProps, closeModal: handleSuccessModalClose },
    errorModalProps,
    loadPartnerConfigErrorModal,
    cancellationModalProps,
    handleCancelForm,
    setInitialFormValues
  }
}
